import React, { useEffect, useRef } from 'react';
import ChatItem from './ChatItem';
import SearchBar from './SearchBar';
import { Support, Student } from '../messages.types';
import { isFunction } from '@/utility';

interface ChatListProps {
	supports?: Support[];
	students?: Student[];
	filteredStudents?: Student[];
	selectedItemId?: number | string;
	onSelectItem: (id: number, isSupport: boolean) => void;
	onSearch?: (query: string) => void;
	onLoadMore?: () => void;
	hasMore?: boolean;
	isLoading?: boolean;
	searchQuery?: string;
}

const ChatList: React.FC<ChatListProps> = ({ supports = [], students = [], selectedItemId, onSelectItem, filteredStudents, onSearch = () => {}, onLoadMore, hasMore, isLoading, searchQuery }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!isFunction(onLoadMore)) {
			return;
		}

		if (!sentinelRef.current) {
			return;
		}

		if (!hasMore) {
			return;
		}

		const observer = new IntersectionObserver(
			entries => {
				const first = entries[0];
				if (first.isIntersecting && hasMore && !isLoading) {
					onLoadMore();
				}
			},
			{
				root: containerRef.current,
				rootMargin: '50px',
				threshold: 0
			}
		);

		const currentSentinel = sentinelRef.current;
		observer.observe(currentSentinel);

		return () => {
			if (currentSentinel) {
				observer.unobserve(currentSentinel);
			}
			observer.disconnect();
		};
	});

	const list = filteredStudents ?? students;
	return (
		<div className="messages-sidebar">
			{supports && supports.length > 0 && (
				<div className="support-section">
					<div className="section-header">
						<span>Destek</span>
					</div>
					<div className="section-list">
						{supports.map(support => (
							<ChatItem
								key={`support-${support.id}`}
								item={support}
								isActive={selectedItemId === support.id}
								onClick={() => onSelectItem(support.id as number, true)}
								isSupport={true}
							/>
						))}
					</div>
				</div>
			)}
			<div className="students-section">
				<div className="section-header">
					<span>
						Öğrenciler <span className="student-length">({list.length})</span>
					</span>
				</div>

				<SearchBar
					placeholder="Öğrenci Ara"
					onSearch={onSearch}
				/>

				<div
					className="section-list"
					ref={containerRef}>
					{list.map((student, index) => (
						<ChatItem
							key={`student-${student.id}-${index}`}
							item={student}
							isActive={selectedItemId === student.id}
							onClick={() => onSelectItem(student?.id, false)}
						/>
					))}

					{filteredStudents?.length === 0 && <div className="no-student"> Öğrenci bulunamadı! </div>}

					{hasMore && (
						<div
							ref={sentinelRef}
							className={'has-more-container'}
						/>
					)}

					{isLoading && <div className="loading-more">Yükleniyor…</div>}

					{!hasMore && !isLoading && list.length > 0 && searchQuery?.length === 0 && <div className="end-of-list">Tüm Mesajlar Yüklendi</div>}
				</div>
			</div>
		</div>
	);
};

export default ChatList;
